import React, { useCallback, useEffect, useRef, useState } from 'react'
import AdminSidebar from './AdminSidebar'
import AdminTopbar from './AdminTopbar'
import OzontelIframe from './OzontelIframe'
import IncomingCallActionModal from './IncomingCallActionModal'
import AgentWorkspacePage from './pages/AgentWorkspacePage'
import TicketsPage from './pages/TicketsPage'
import TicketDetail from './pages/TicketDetail'
import CreateTicketPage from './pages/CreateTicketPage'
import Vehicle360Page from './pages/Vehicle360Page'
import TicketAssignmentsPage   from './pages/TicketAssignmentsPage'
import AdminPage from './pages/AdminPage'
import TicketAssignmentsDetail from './pages/TicketAssignmentsDetail'
import SettingsPage from './pages/SettingsPage'
import AgentPage from './pages/AgentPage'
import UploaderPage from './pages/UploaderPage'
import TripHistoryPage from './pages/TripHistoryPage'
import '../styles/AdminLayout.css'

const WORKSPACE_PAGE_PARAM = 'supportPage'
const TICKET_ID_PARAM = 'ticketId'

// Strips everything but digits/'+' from a raw dialer number and ensures a
// "+91" country code, so API calls send a clean "+91XXXXXXXXXX" — never
// spaces/dashes/parens from the raw Ozonetel payload.
const normalizePhoneForApi = (raw) => {
  const cleaned = String(raw || '').replace(/[^\d+]/g, '')
  if (!cleaned) return ''
  if (cleaned.startsWith('+')) return cleaned
  if (cleaned.startsWith('91') && cleaned.length === 12) return `+${cleaned}`
  if (cleaned.startsWith('0') && cleaned.length === 11) return `+91${cleaned.slice(1)}`
  if (cleaned.length === 10) return `+91${cleaned}`
  return `+${cleaned}`
}

// Writes a small spinner into a freshly `window.open`-ed tab so it isn't a
// blank white page while we wait on an API call before knowing its real URL.
// (The tab has to be opened synchronously, before any `await`, or popup
// blockers kill it — so it necessarily sits blank for a moment either way.)
const writeLoadingTab = (tab, message) => {
  if (!tab || tab.closed) return
  try {
    tab.document.write(`<!doctype html><html><head><title>Loading…</title><meta charset="utf-8"><style>
      html,body{height:100%;margin:0;}
      body{display:flex;align-items:center;justify-content:center;background:#F8FAFC;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;}
      .wrap{display:flex;flex-direction:column;align-items:center;gap:14px;color:#6B7280;font-size:14px;}
      .spinner{width:34px;height:34px;border:3px solid #E0E7EF;border-top-color:#0090D9;border-radius:50%;animation:spin .7s linear infinite;}
      @keyframes spin{to{transform:rotate(360deg)}}
    </style></head><body><div class="wrap"><div class="spinner"></div><div>${message}</div></div></body></html>`)
    tab.document.close()
  } catch { }
}

const getInitialPage = (currentUser) => {
  if (typeof window === 'undefined') return 'agent'
  const params = new URLSearchParams(window.location.search)
  const page = params.get(WORKSPACE_PAGE_PARAM)
  if (page) return page
  const featureAccess = currentUser?.feature_access || []
  if (featureAccess.includes('ert_page')) return 'tickets'
  return 'agent'
}

const AdminLayout = ({ currentUser, fetchJson, onLogout, onBack }) => {
  // Suppress the Ozonetel iframe in tabs that are auto-opened for create ticket
  // (prevents a second Ozonetel session which kicks the agent out of the first tab)
  const isNoDialer = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('nodialer') === '1'

  const [currentPage, setCurrentPage] = useState(() => getInitialPage(currentUser))
  const [filterDropdown, setFilterDropdown] = useState(false)
  const [notifications, setNotifications] = useState(false)
  const [waChat, setWaChat] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState('overall')
  const [selectedTicket, setSelectedTicket] = useState(() => {
    if (typeof window === 'undefined') return null
    const params = new URLSearchParams(window.location.search)
    const page = params.get(WORKSPACE_PAGE_PARAM)
    const id   = params.get(TICKET_ID_PARAM)
    if ((page === 'ticket-detail' || page === 'assignment-ticket-detail') && id) {
      return { ticket_id: id }
    }
    return null
  })
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [dateFilter, setDateFilter] = useState({ period: 'today', label: 'Today', fromDate: '', toDate: '' })
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [dialerOpen, setDialerOpen] = useState(false)
  const [activeCall, setActiveCall] = useState(null)   // { callLogId, connectionType, customerPhone, ucid, campaignName }
  const [showCallPopup, setShowCallPopup] = useState(false)
  // Inbound-call decision flow: recent-open-ticket lookup + which action (if any) is in flight
  const [recentTicketCheck, setRecentTicketCheck] = useState(null) // { loading, ticket, error }
  const [callActionSubmitting, setCallActionSubmitting] = useState('') // '' | 'followup' | 'new_ticket' | 'query'
  const [callActionResult, setCallActionResult] = useState(null) // { ok, message, url? } — url set if window.open was blocked, for a manual retry link
  const [ozonetelLoggedIn, setOzonetelLoggedIn] = useState(null) // null=unknown, true=logged in, false=logged out
  const VALID_STATUSES = ['ready', 'busy', 'short_break', 'lunch', 'training_session', 'quality_feedback', 'offline']
  const normalizeStatus = (s) => { const v = String(s || '').toLowerCase(); return VALID_STATUSES.includes(v) ? v : 'ready' }
  const [agentStatus, setAgentStatus] = useState(() => normalizeStatus(currentUser?.agent_state))
  useEffect(() => {
    if (currentUser?.agent_state) setAgentStatus(normalizeStatus(currentUser.agent_state))
  }, [currentUser?.agent_state])
  const callLogIdRef = useRef('')
  const fetchJsonRef = useRef(fetchJson)
  useEffect(() => { fetchJsonRef.current = fetchJson }, [fetchJson])
  // ref so the stable empty-deps useCallback can always reach the latest setter
  const agentStatusSetterRef = useRef(setAgentStatus)
  useEffect(() => { agentStatusSetterRef.current = setAgentStatus }, [])
  const isFirstPageChange = useRef(true)
  const isPopStateNav = useRef(false)
  const prevPageRef = useRef(getInitialPage())

  // When the call tab submits and clears localStorage, clear active call state in this tab
  useEffect(() => {
    function handleStorage(e) {
      if (e.key === 'ozonetel_call_log_id' && e.newValue === null) {
        callLogIdRef.current = ''
        setActiveCall(null)
        setShowCallPopup(false)
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  const handleLogout = useCallback(async () => {
    if (fetchJsonRef.current) {
      try {
        await fetchJsonRef.current('/api/agents/agent-state/change-agent-state/?action=dialer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ state: 'Offline' }),
        })
      } catch { }
    }
    onLogout?.()
  }, [onLogout])

  const clearCallState = useCallback(() => {
    callLogIdRef.current = ''
    setActiveCall(null)
    setShowCallPopup(false)
    setRecentTicketCheck(null)
    setCallActionSubmitting('')
  }, [])

  // Guard navigation away from the create-ticket page when there's an unsubmitted call-auto-ticket
  const guardedSetCurrentPage = useCallback((page) => {
    if (currentPage === 'create') {
      try {
        const autoTicket = JSON.parse(localStorage.getItem('ozonetel_auto_ticket') || '{}')
        if (autoTicket?.id || autoTicket?.ticket_id) {
          const ok = window.confirm(
            'Are you sure you want to leave? The call ticket will not be created and the call data will be removed.'
          )
          if (!ok) return
          localStorage.removeItem('ozonetel_auto_ticket')
          localStorage.removeItem('ozonetel_call_log_id')
          localStorage.removeItem('ozonetel_call_data')
        }
      } catch { }
    }
    setCurrentPage(page)
  }, [currentPage])

  // Legacy auto-create-ticket-and-open-tab flow (used for outbound calls,
  // and as a fallback for inbound calls when the recent-open-ticket lookup fails).
  const legacyAutoCreateTicket = useCallback((callLogId, fetchJson) => {
    const newTab = window.open('about:blank', '_blank')
    writeLoadingTab(newTab, 'Creating ticket…')

    if (fetchJson && callLogId) {
      fetchJson('/api/tickets/ticket/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ call_log_id: callLogId }),
      })
        .then(res => {
          console.log('[Ozonetel] auto ticket created', res)
          localStorage.setItem('ozonetel_auto_ticket', JSON.stringify(res))
          setActiveCall(prev => ({
            ...(prev || {}),
            ticketNumber: res?.ticket_number || '',
            customerName: res?.customer_details?.name || '',
            customerPhone: res?.customer_details?.phone || prev?.customerPhone || '',
          }))

          // Always show popup in the main tab with call details
          setShowCallPopup(true)

          if (newTab && !newTab.closed) {
            const url = new URL(window.location.href)
            url.search = ''
            url.searchParams.set('supportPage', 'create')
            url.searchParams.set('source', 'call')
            url.searchParams.set('nodialer', '1')  // prevent second Ozonetel session
            if (res?.ticket_number) url.searchParams.set('ticket_number', res.ticket_number)
            newTab.location.href = url.toString()
          }
        })
        .catch(err => {
          console.error('[Ozonetel] auto ticket error', err)
          if (newTab && !newTab.closed) newTab.close()
          setShowCallPopup(true)
        })
    } else {
      if (newTab) newTab.close()
      setShowCallPopup(true)
    }
  }, [])

  const handleOzonetelEvent = useCallback(async (e) => {
    let data = e.data
    if (typeof data === 'string') {
      try { data = JSON.parse(data) } catch { }
    }

    if (!data || typeof data !== 'object') return

    // Ozonetel uses `action` field (not `type`/`event`)
    // Actual call details are nested inside `token`
    const evtType = data.action || data.type || data.event || ''
    const token = data.token || {}

    // Ignore heartbeat / empty pings that Ozonetel sends every second
    if (!evtType) return

    console.log('[Ozonetel event]', evtType, token)

    // Track dialer login state
    if (evtType === 'logout') {
      setOzonetelLoggedIn(false)
    } else {
      setOzonetelLoggedIn(true)
    }

    if (evtType === 'newCall') {
      const fetchJson = fetchJsonRef.current
      if (!fetchJson) { console.warn('[Ozonetel] fetchJson not ready'); return }
      try {
        // "IncomingCall" → inbound, "Manual Dialing" / anything else → outbound
        const connectionType = token.callType === 'IncomingCall' ? 'inbound' : 'outbound'
        // Auto-open the dialer panel for inbound calls so the agent can see it
        if (connectionType === 'inbound') setDialerOpen(true)
        const payload = {
          customer_phone: token.callerId || '',
          connection_type: connectionType,
          ucid: token.ucid || '',
          monitor_ucid: token.monitorUcid || token.ucid || '',
          campaign_name: token.campaignName || '',
          did_number: token.did || '',
        }
        console.log('[Ozonetel] calling call-log API', payload)
        const res = await fetchJson('/api/calls/call-log/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        console.log('[Ozonetel] call-log response', res)
        fetchJson('/api/agents/agent-state/change-agent-state/?action=dialer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ state: 'Busy' }),
        }).then(stateRes => {
          const STATUS_MAP = {
            ready: 'ready', busy: 'busy', short_break: 'short_break',
            lunch: 'lunch', training_session: 'training_session',
            quality_feedback: 'quality_feedback', offline: 'offline',
          }
          const mapped = STATUS_MAP[String(stateRes?.status || '').toLowerCase()]
          if (mapped) agentStatusSetterRef.current(mapped)
        }).catch(err => console.error('[Ozonetel] newCall state sync error', err))
        const callLogId = String(res?.call_log_id || res?.id || '')
        const stored = {
          callLogId,
          connectionType,
          customerPhone: token.callerId || '',
          ucid: token.ucid || '',
          campaignName: token.campaignName || '',
          didNumber: token.did || '',
          eventData: token,
        }
        callLogIdRef.current = callLogId
        setActiveCall(stored)
        setShowCallPopup(false)
        localStorage.setItem('ozonetel_call_log_id', callLogId)
        localStorage.setItem('ozonetel_call_data', JSON.stringify(stored))
      } catch (err) {
        console.error('[Ozonetel] call-log API error', err)
      }
    }

    if (evtType === 'busyAgent') {
      const callLogId = callLogIdRef.current || localStorage.getItem('ozonetel_call_log_id') || ''
      const fetchJson = fetchJsonRef.current
      let storedCallData = {}
      try { storedCallData = JSON.parse(localStorage.getItem('ozonetel_call_data') || '{}') } catch { }
      const connType = storedCallData.connectionType || 'inbound'

      if (connType !== 'inbound') {
        // Outbound calls: unchanged legacy behavior — auto-create the stub
        // ticket and open a new tab straight to the create-ticket form.
        legacyAutoCreateTicket(callLogId, fetchJson)
        return
      }

      // Inbound calls: check for a recent open ticket for this caller before
      // creating anything — surfaces a decision modal instead.
      setRecentTicketCheck({ loading: true, ticket: null, error: null })
      setShowCallPopup(true)
      const phone = normalizePhoneForApi(storedCallData.customerPhone)
      if (!fetchJson || !phone) {
        setRecentTicketCheck({ loading: false, ticket: null, error: null })
        return
      }
      // URLSearchParams/encodeURIComponent both percent-encode '+' to %2B — keep
      // it literal since the backend expects "+91..." in the query string.
      const phoneParam = encodeURIComponent(phone).replace(/%2B/g, '+')
      fetchJson(`/api/tickets/ticket/recent-open-ticket/?phone=${phoneParam}`)
        .then(res => {
          setRecentTicketCheck({ loading: false, ticket: res?.ticket || null, vin: res?.vin || '', checkTime: res?.check_time || '', error: null })
        })
        .catch(err => {
          console.error('[Ozonetel] recent-open-ticket error', err)
          // Don't block the agent if the lookup fails — fall back to the
          // original auto-create-and-open-tab flow.
          setRecentTicketCheck(null)
          setShowCallPopup(false)
          legacyAutoCreateTicket(callLogId, fetchJson)
        })
    }
    if (evtType === 'logout') {
      const fetchJson = fetchJsonRef.current
      if (!fetchJson) return
      fetchJson('/api/agents/agent-state/change-agent-state/?action=dialer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state: 'Offline' }),
      }).then(stateRes => {
        const STATUS_MAP = {
          ready: 'ready', busy: 'busy', short_break: 'short_break',
          lunch: 'lunch', training_session: 'training_session',
          quality_feedback: 'quality_feedback', offline: 'offline',
        }
        const mapped = STATUS_MAP[String(stateRes?.status || '').toLowerCase()]
        if (mapped) agentStatusSetterRef.current(mapped)
      }).catch(err => console.error('[Ozonetel] logout state sync error', err))
    }

    if (evtType === 'dropCall') {
      const dialStatus = data.dialStatus || token.dialStatus || ''
      const shouldSetReady = dialStatus === 'NormalUnspecified' || dialStatus === 'agent_disconnect'
      if (!shouldSetReady) return
      const fetchJson = fetchJsonRef.current
      if (!fetchJson) return
      fetchJson('/api/agents/agent-state/change-agent-state/?action=dialer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state: 'Ready' }),
      }).then(stateRes => {
        const STATUS_MAP = {
          ready: 'ready', busy: 'busy', short_break: 'short_break',
          lunch: 'lunch', training_session: 'training_session',
          quality_feedback: 'quality_feedback', offline: 'offline',
        }
        const mapped = STATUS_MAP[String(stateRes?.status || '').toLowerCase()]
        if (mapped) agentStatusSetterRef.current(mapped)
      }).catch(err => console.error('[Ozonetel] dropCall state sync error', err))
    }

    if (evtType === 'changeState') {
      const fetchJson = fetchJsonRef.current
      if (!fetchJson) return
      // API status values → STATUS_OPTIONS keys (1-to-1 now that UI uses training_session)
      const STATUS_MAP = {
        ready: 'ready', busy: 'busy', short_break: 'short_break',
        lunch: 'lunch', training_session: 'training_session',
        quality_feedback: 'quality_feedback', offline: 'offline',
      }
      try {
        const res = await fetchJson('/api/agents/agent-state/change-agent-state/?action=dialer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(token),
        })
        const mapped = STATUS_MAP[String(res?.status || '').toLowerCase()]
        if (mapped) agentStatusSetterRef.current(mapped)
      } catch (err) {
        console.error('[Ozonetel] changeState sync error', err)
      }
    }
  }, []) // stable — all external values accessed via refs or stable setters

  /* ── inbound-call decision modal actions ── */
  const buildCreateTicketTabUrl = (ticketNumber, extraParams = {}) => {
    const url = new URL(window.location.href)
    url.search = ''
    url.searchParams.set('supportPage', 'create')
    url.searchParams.set('source', 'call')
    url.searchParams.set('nodialer', '1')
    if (ticketNumber) url.searchParams.set('ticket_number', ticketNumber)
    Object.entries(extraParams).forEach(([k, v]) => url.searchParams.set(k, v))
    return url.toString()
  }

  const handleCallNewTicketOrQuery = async (mode) => {
    const callLogId = callLogIdRef.current || localStorage.getItem('ozonetel_call_log_id') || ''
    const fetchJson = fetchJsonRef.current
    if (!fetchJson || !callLogId) return
    setCallActionSubmitting(mode === 'query' ? 'query' : 'new_ticket')
    const tab = window.open('about:blank', '_blank')
    writeLoadingTab(tab, 'Creating ticket…')
    try {
      const res = await fetchJson('/api/tickets/ticket/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ call_log_id: callLogId }),
      })
      localStorage.setItem('ozonetel_auto_ticket', JSON.stringify(res))
      setActiveCall(prev => ({
        ...(prev || {}),
        ticketNumber: res?.ticket_number || '',
        customerName: res?.customer_details?.name || '',
        customerPhone: res?.customer_details?.phone || prev?.customerPhone || '',
      }))
      const extra = mode === 'query' ? { mode: 'query' } : {}
      const url = buildCreateTicketTabUrl(res?.ticket_number, extra)
      if (tab && !tab.closed) tab.location.href = url
      else window.open(url, '_blank', 'noopener')
    } catch (err) {
      console.error('[Ozonetel] auto ticket error', err)
      if (tab && !tab.closed) tab.close()
    } finally {
      setCallActionSubmitting('')
      setShowCallPopup(false)
      setRecentTicketCheck(null)
    }
  }

  // On success, navigate THIS tab (no new tab/window) straight to the
  // ticket's Follow-ups tab. We set `tab`/`followupLogged` on the URL via
  // history.replaceState *before* flipping React state — the page-sync
  // effect below only manages `supportPage`/`ticketId` on top of whatever's
  // already in the URL, so these two params survive that effect and are
  // there in time for TicketDetail to read them on its very first render.
  const handleCallFollowUp = async () => {
    const callLogId = callLogIdRef.current || localStorage.getItem('ozonetel_call_log_id') || ''
    const fetchJson = fetchJsonRef.current
    const ticketId = recentTicketCheck?.ticket?.ticket_information?.id
    if (!fetchJson || !ticketId) return
    setCallActionSubmitting('followup')
    setCallActionResult(null)
    try {
      await fetchJson(`/api/tickets/ticket/${ticketId}/ticket-follow-up/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ call_log_id: callLogId }),
      })
      const url = new URL(window.location.href)
      url.searchParams.set('tab', 'followups')
      url.searchParams.set('followupLogged', '1')
      window.history.replaceState({}, '', url.toString())
      setSelectedTicket({ ticket_id: ticketId })
      setCurrentPage('ticket-detail')
      setCallActionSubmitting('')
      setCallActionResult(null)
      clearCallState()
    } catch (err) {
      console.error('[Ozonetel] follow-up error', err)
      setCallActionSubmitting('')
      setCallActionResult({ ok: false, message: 'Failed to log follow-up. Please try again.' })
    }
  }

  useEffect(() => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    if (currentPage === 'agent') {
      url.searchParams.delete(WORKSPACE_PAGE_PARAM)
      url.searchParams.delete(TICKET_ID_PARAM)
    } else {
      url.searchParams.set(WORKSPACE_PAGE_PARAM, currentPage)
      if (currentPage === 'ticket-detail' || currentPage === 'assignment-ticket-detail') {
        const id = selectedTicket?.ticket_id
        if (id != null && id !== '') {
          url.searchParams.set(TICKET_ID_PARAM, String(id))
        }
      } else {
        url.searchParams.delete(TICKET_ID_PARAM)
      }
    }
    const pageChanged = prevPageRef.current !== currentPage
    prevPageRef.current = currentPage

    if (isFirstPageChange.current) {
      isFirstPageChange.current = false
      window.history.replaceState({ page: currentPage }, '', url.toString())
    } else if (isPopStateNav.current) {
      // Back/forward button triggered this change — browser already moved in history,
      // just sync the URL without pushing a new entry
      isPopStateNav.current = false
      window.history.replaceState({ page: currentPage }, '', url.toString())
    } else if (pageChanged) {
      // Real user navigation — add a history entry
      window.history.pushState({ page: currentPage }, '', url.toString())
    } else {
      // Only ticket ID changed (same page) — update current entry, no new history entry
      window.history.replaceState({ page: currentPage }, '', url.toString())
    }
  }, [currentPage, selectedTicket?.ticket_id])

  /* ── handle browser back / forward ── */
  useEffect(() => {
    function handlePopState() {
      const params = new URLSearchParams(window.location.search)
      const page = params.get(WORKSPACE_PAGE_PARAM) || 'agent'
      const ticketId = params.get(TICKET_ID_PARAM)
      isPopStateNav.current = true
      if ((page === 'ticket-detail' || page === 'assignment-ticket-detail') && ticketId) {
        setSelectedTicket(prev => (prev?.ticket_id === ticketId ? prev : { ticket_id: ticketId }))
      }
      setCurrentPage(page)
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const renderPage = () => {
    const featureAccess = currentUser?.feature_access
    if (Array.isArray(featureAccess) && featureAccess.length === 0) {
      return (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 16, padding: 32, textAlign: 'center',
        }}>
          <div style={{ fontSize: 48 }}>🔒</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>
            No Access
          </div>
          <div style={{ fontSize: 14, color: '#6B7280', maxWidth: 360, lineHeight: 1.6 }}>
            You don't have permission to access the CRM. Please contact your administrator to get the required access.
          </div>
        </div>
      )
    }

    switch(currentPage) {
      case 'agent':
        return <AgentWorkspacePage currentUser={currentUser} fetchJson={fetchJson} setCurrentPage={guardedSetCurrentPage} setSelectedTicket={setSelectedTicket} dateFilter={dateFilter} agentStatus={agentStatus} setAgentStatus={setAgentStatus} ozonetelLoggedIn={ozonetelLoggedIn} />
      case 'tickets':
        return <TicketsPage setCurrentPage={guardedSetCurrentPage} setSelectedTicket={setSelectedTicket} fetchJson={fetchJson} currentUser={currentUser} />
      case 'ticket-detail':
        return <TicketDetail ticket={selectedTicket} setCurrentPage={guardedSetCurrentPage} fetchJson={fetchJson} setSelectedVehicle={setSelectedVehicle} currentUser={currentUser} />
      case 'assignment-ticket-detail':
        return <TicketAssignmentsDetail ticket={selectedTicket} setCurrentPage={guardedSetCurrentPage} fetchJson={fetchJson} setSelectedVehicle={setSelectedVehicle} />
      case 'create':
        return <CreateTicketPage fetchJson={fetchJson} setCurrentPage={guardedSetCurrentPage} setSelectedVehicle={setSelectedVehicle} setSelectedTicket={setSelectedTicket} callLogId={activeCall?.callLogId || ''} fromCallProp={!!activeCall?.callLogId} />
      case 'agent_page':
        return <AgentPage fetchJson={fetchJson} />
      case 'uploader':
        return <UploaderPage fetchJson={fetchJson} />
      case 'admin_page':
        return <AdminPage fetchJson={fetchJson} />
      case 'ticket_assignments':
        return <TicketAssignmentsPage setCurrentPage={guardedSetCurrentPage} setSelectedTicket={setSelectedTicket} fetchJson={fetchJson} />
      case 'settings':
        return <SettingsPage fetchJson={fetchJson} />
      case 'trip-history':
        return <TripHistoryPage fetchJson={fetchJson} currentUser={currentUser} />
      case 'vehicle-360': {
        const vehicle = selectedVehicle || (() => {
          try { return JSON.parse(localStorage.getItem('crm_vehicle_360_data') || 'null') } catch { return null }
        })()
        return (
          <Vehicle360Page
            fetchJson={fetchJson}
            selectedVehicle={vehicle}
            setSelectedVehicle={setSelectedVehicle}
            setCurrentPage={setCurrentPage}
            setSelectedTicket={setSelectedTicket}
          />
        )
      }
      default:
        return <AgentWorkspacePage />
    }
  }

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <AdminSidebar
        currentPage={currentPage}
        currentUser={currentUser}
        setCurrentPage={guardedSetCurrentPage}
        onLogout={handleLogout}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />

      {/* Ozonetel dialer panel — skipped in nodialer tabs to prevent double Ozonetel sessions */}
      {!isNoDialer && <div style={{
          width: dialerOpen ? 320 : 0,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRight: dialerOpen ? '1px solid #D1E4F3' : 'none',
          boxShadow: dialerOpen ? '4px 0 16px rgba(0,0,0,.10)' : 'none',
          background: '#fff',
          zIndex: 200,
          overflow: 'hidden',
          transition: 'width .2s ease',
        }}>
          {/* Panel header */}
          <div style={{
            background: 'linear-gradient(135deg, #1A6FA8 0%, #2B8FD8 100%)',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#fff" strokeWidth="1.8">
                  <path d="M6.5 4S5 4 4 6s1 5 3 7 5 4 7 3 2-2.5 2-2.5l-3-3-2 1-3-4 1-2-3-3z" />
                </svg>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Dialer</span>
              </div>
              <div style={{ color: 'rgba(255,255,255,.75)', fontSize: 11, marginTop: 3 }}>
                Make calls to customers
              </div>
            </div>
            <button
              onClick={() => setDialerOpen(false)}
              style={{
                background: 'rgba(255,255,255,.2)',
                border: 'none',
                borderRadius: 6,
                width: 26,
                height: 26,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 16,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>

          {/* Ozonetel iframe fills the rest */}
          <OzontelIframe
            style={{ flex: 1, minHeight: 0 }}
            height="100%"
            onEvent={handleOzonetelEvent}
          />
        </div>}

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", position: 'relative' }}>

        {/* Inbound-call decision modal — recent-open-ticket check drives follow-up vs new-ticket/query */}
        {showCallPopup && activeCall && recentTicketCheck && (
          <IncomingCallActionModal
            activeCall={activeCall}
            recentTicketCheck={recentTicketCheck}
            submitting={callActionSubmitting}
            result={callActionResult}
            onFollowUp={handleCallFollowUp}
            onNewTicket={() => handleCallNewTicketOrQuery('new_ticket')}
            onQuery={() => handleCallNewTicketOrQuery('query')}
          />
        )}

        {/* Active call popup — outbound calls / inbound fallback when the recent-open-ticket lookup fails */}
        {showCallPopup && activeCall && !recentTicketCheck && (
          <div style={{
            position: 'absolute', top: 60, right: 16, zIndex: 500,
            width: 300, background: '#fff', borderRadius: 10,
            boxShadow: '0 8px 32px rgba(0,0,0,.18)', border: '1px solid #E0E7EF',
            overflow: 'hidden', animation: 'slideInRight .2s ease',
          }}>
            {/* Popup header */}
            <div style={{ background: 'linear-gradient(135deg,#16A34A,#22C55E)', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', opacity: .9, animation: 'pulse 1.2s infinite' }} />
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>Active Call Connected</span>
              </div>
              <button onClick={() => setShowCallPopup(false)} style={{ background: 'rgba(255,255,255,.25)', border: 'none', borderRadius: 4, width: 22, height: 22, cursor: 'pointer', color: '#fff', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>
            {/* Popup body */}
            <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { label: 'Customer', value: activeCall.customerName || activeCall.customerPhone || '—' },
                { label: 'Phone', value: activeCall.customerPhone || '—' },
                { label: 'Campaign', value: activeCall.campaignName || '—' },
                { label: 'Ticket #', value: activeCall.ticketNumber || '—' },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                  <span style={{ color: '#6B7280', fontWeight: 500 }}>{label}</span>
                  <span style={{ color: '#111827', fontWeight: 600, fontFamily: 'monospace', fontSize: 11 }}>{value}</span>
                </div>
              ))}
              <button
                onClick={() => {
                  const url = new URL(window.location.href)
                  url.searchParams.set('supportPage', 'create')
                  url.searchParams.set('source', 'call')
                  url.searchParams.set('nodialer', '1')
                  if (activeCall?.ticketNumber) url.searchParams.set('ticket_number', activeCall.ticketNumber)
                  window.open(url.toString(), '_blank', 'noopener')
                }}
                style={{
                  marginTop: 6, padding: '10px 0', width: '100%',
                  background: '#0090D9', color: '#fff', border: 'none',
                  borderRadius: 7, cursor: 'pointer', fontWeight: 700,
                  fontSize: 13, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 6,
                }}
              >
                <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="#fff" strokeWidth="2.2"><path d="M10 5v10M5 10h10" /></svg>
                Create Ticket — opens in new tab
              </button>
            </div>
          </div>
        )}

        {(
          <AdminTopbar
            currentPage={currentPage}
            setCurrentPage={guardedSetCurrentPage}
            notifications={notifications}
            setNotifications={setNotifications}
            fetchJson={fetchJson}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            setSelectedTicket={setSelectedTicket}
            setSelectedVehicle={setSelectedVehicle}
            dialerOpen={dialerOpen}
            setDialerOpen={setDialerOpen}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            clearCallState={clearCallState}
          />
        )}


        {['ticket-detail', 'assignment-ticket-detail', 'vehicle-360', 'create'].includes(currentPage) && (
          <div style={{ padding: '8px 18px 0', flexShrink: 0 }}>
            <button
              onClick={() => window.history.back()}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'none', border: '1px solid #E0E7EF',
                borderRadius: 7, padding: '5px 12px',
                cursor: 'pointer', fontSize: 13, color: '#374151',
                fontWeight: 500,
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#F1F5F9'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 10H5M10 5l-5 5 5 5" />
              </svg>
              Back
            </button>
          </div>
        )}

        <div style={{ flex: 1, overflow: "auto", padding: "18px" }}>

          {renderPage()}
        </div>
      </div>
    </div>
  )
}

export default AdminLayout
